import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Search, Filter, ShoppingBag } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '../components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '../components/ui/dropdown-menu';
import { MoreHorizontal, Edit, Trash2 } from 'lucide-react';
import { watchesAPI, ordersAPI } from '../utils/api';
import ConfirmDelete from '../components/ConfirmDelete';
import { toast } from 'sonner';

const Watches = () => {
  const [watches, setWatches] = useState([]);
  const [filteredWatches, setFilteredWatches] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [brandFilter, setBrandFilter] = useState('all');
  const [conditionFilter, setConditionFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [soldDialog, setSoldDialog] = useState(false);
  const [soldForm, setSoldForm] = useState({ watchId: '', customerName: '', customerPhone: '', notes: '' });
  const [marking, setMarking] = useState(false);
  const navigate = useNavigate();

  const loadWatches = async () => {
    try {
      const data = await watchesAPI.getAll();
      setWatches(data);
    } catch (error) {
      console.error('Failed to load watches:', error);
      toast.error('Failed to load watches');
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = useCallback(() => {
    let filtered = [...watches];
    if (searchTerm) {
      filtered = filtered.filter(
        (w) =>
          w.model.toLowerCase().includes(searchTerm.toLowerCase()) ||
          w.brand.toLowerCase().includes(searchTerm.toLowerCase()) ||
          w.referenceNumber?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    if (brandFilter !== 'all') {
      filtered = filtered.filter((w) => w.brand === brandFilter);
    }
    if (conditionFilter !== 'all') {
      filtered = filtered.filter((w) => w.condition === conditionFilter);
    }
    setFilteredWatches(filtered);
  }, [watches, searchTerm, brandFilter, conditionFilter]);

  useEffect(() => { loadWatches(); }, []); // Initial load only — loadWatches is stable
  useEffect(() => { applyFilters(); }, [applyFilters]);

  const handleDelete = async (id) => {
    try {
      await watchesAPI.delete(id);
      loadWatches();
      toast.success('Watch deleted successfully');
    } catch (error) {
      console.error('Failed to delete watch:', error);
      toast.error('Failed to delete watch');
    }
  };

  const handleMarkSold = async (e) => {
    e.preventDefault();
    if (!soldForm.watchId) { toast.error('Select a watch'); return; }
    setMarking(true);
    try {
      const watch = watches.find(w => w.id === soldForm.watchId);
      await ordersAPI.create({
        customerName: soldForm.customerName.trim() || 'Walk-in Customer',
        customerPhone: soldForm.customerPhone.trim(),
        listingId: soldForm.watchId,
        listingTitle: watch ? `${watch.brand} ${watch.model}` : '',
        totalPrice: watch?.price || 0,
        notes: soldForm.notes.trim(),
        source: 'offline',
        status: 'delivered',
      });
      toast.success(`${watch?.brand} ${watch?.model} marked as sold & delivered!`);
      setSoldDialog(false);
      setSoldForm({ watchId: '', customerName: '', customerPhone: '', notes: '' });
      loadWatches();
    } catch (e) { toast.error(e.message || 'Failed'); }
    finally { setMarking(false); }
  };

  const brands = [...new Set(watches.map((w) => w.brand).filter(Boolean))];
  const conditions = ['New', 'Excellent', 'VeryGood'];

  if (loading) {
    return <div className="flex items-center justify-center min-h-screen">Loading...</div>;
  }

  return (
    <div className="space-y-4 sm:space-y-6" data-testid="watches-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Watches</h1>
          <p className="text-muted-foreground mt-1 text-sm sm:text-base">Manage your luxury watch inventory</p>
        </div>
        <div className="flex gap-2 w-full sm:w-auto">
          <Button onClick={() => setSoldDialog(true)} variant="outline" className="flex-1 sm:flex-none border-green-500/50 text-green-400 hover:bg-green-500/10" data-testid="mark-sold-button">
            <ShoppingBag className="h-4 w-4 mr-2" /> Mark as Sold
          </Button>
          <Button onClick={() => navigate('/watches/new')} data-testid="create-watch-button" className="bg-primary text-primary-foreground hover:bg-[hsl(42,44%,62%)] flex-1 sm:flex-none">
            <Plus className="h-4 w-4 mr-2" /> Add Watch
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card className="p-3 sm:p-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search watches..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              data-testid="watch-search-input"
              className="pl-9"
            />
          </div>
          <Select value={brandFilter} onValueChange={setBrandFilter}>
            <SelectTrigger data-testid="brand-filter">
              <SelectValue placeholder="All Brands" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Brands</SelectItem>
              {brands.map((brand) => (
                <SelectItem key={brand} value={brand}>
                  {brand}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={conditionFilter} onValueChange={setConditionFilter}>
            <SelectTrigger data-testid="condition-filter">
              <SelectValue placeholder="All Conditions" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Conditions</SelectItem>
              {conditions.map((cond) => (
                <SelectItem key={cond} value={cond}>
                  {cond}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="flex items-center text-sm text-muted-foreground">
            <Filter className="h-4 w-4 mr-2" />
            {filteredWatches.length} of {watches.length} watches
          </div>
        </div>
      </Card>

      {/* Table */}
      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader className="bg-muted/50">
              <TableRow>
                <TableHead className="w-[80px]">Image</TableHead>
                <TableHead className="min-w-[120px]">Brand</TableHead>
                <TableHead className="min-w-[120px]">Model</TableHead>
                <TableHead className="min-w-[100px]">Reference</TableHead>
                <TableHead className="min-w-[100px]">Condition</TableHead>
                <TableHead className="min-w-[100px]">Price</TableHead>
                <TableHead className="min-w-[100px]">Cost</TableHead>
                <TableHead className="min-w-[80px]">Stock</TableHead>
                <TableHead className="text-right w-[80px]">Actions</TableHead>
              </TableRow>
            </TableHeader>
          <TableBody>
            {filteredWatches.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} className="text-center py-8 text-muted-foreground text-sm">
                  No watches found
                </TableCell>
              </TableRow>
            ) : (
              filteredWatches.map((watch) => (
                <TableRow
                  key={watch.id}
                  data-testid={`watch-row-${watch.id}`}
                  className="hover:bg-secondary/60 transition-colors duration-150"
                >
                  <TableCell>
                    {watch.images && watch.images[0] ? (
                      <img
                        src={watch.images[0]}
                        alt={watch.model}
                        className="h-12 w-12 rounded-lg object-cover border border-border"
                      />
                    ) : (
                      <div className="h-12 w-12 rounded-lg bg-secondary border border-border flex items-center justify-center text-xs text-muted-foreground">
                        No img
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="font-medium">{watch.brand}</TableCell>
                  <TableCell>{watch.model}</TableCell>
                  <TableCell className="text-muted-foreground font-mono text-xs">
                    {watch.referenceNumber || 'N/A'}
                  </TableCell>
                  <TableCell>
                    <Badge variant="secondary">{watch.condition}</Badge>
                  </TableCell>
                  <TableCell className="font-semibold">${watch.price?.toLocaleString()}</TableCell>
                  <TableCell className="text-muted-foreground">
                    ${watch.costPrice?.toLocaleString() || 'N/A'}
                  </TableCell>
                  <TableCell>
                    <Badge variant={watch.stockQty < 5 ? 'destructive' : 'default'}>
                      {watch.stockQty} {watch.stockQty === 1 ? 'unit' : 'units'}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" data-testid="watch-row-menu-button">
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem
                          data-testid="watch-edit-menu-item"
                          onClick={() => navigate(`/watches/${watch.id}/edit`)}
                        >
                          <Edit className="h-4 w-4 mr-2" />
                          Edit
                        </DropdownMenuItem>
                        <ConfirmDelete
                          trigger={
                            <DropdownMenuItem
                              data-testid="watch-delete-menu-item"
                              onSelect={(e) => e.preventDefault()}
                              className="text-destructive focus:text-destructive"
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              Delete
                            </DropdownMenuItem>
                          }
                          title="Delete Watch"
                          description={`Are you sure you want to delete ${watch.brand} ${watch.model}? This action cannot be undone.`}
                          onConfirm={() => handleDelete(watch.id)}
                        />
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        </div>
      </Card>

      {/* Mark as Sold Dialog */}
      <Dialog open={soldDialog} onOpenChange={setSoldDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShoppingBag className="h-5 w-5 text-green-500" /> Mark as Sold (Offline)
            </DialogTitle>
          </DialogHeader>
          <p className="text-xs text-muted-foreground -mt-2">
            Record an offline/walk-in sale. The watch will be marked as sold & delivered immediately.
          </p>
          <form onSubmit={handleMarkSold} className="space-y-4 pt-2">
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Watch *</Label>
              <Select value={soldForm.watchId} onValueChange={v => setSoldForm(f => ({ ...f, watchId: v }))}>
                <SelectTrigger><SelectValue placeholder="Select a watch to mark as sold" /></SelectTrigger>
                <SelectContent>
                  {watches.filter(w => w.status !== 'sold').map(w => (
                    <SelectItem key={w.id} value={w.id}>
                      {w.brand} {w.model} — ${w.price?.toLocaleString()}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Buyer Name</Label>
                <Input placeholder="Walk-in Customer" value={soldForm.customerName} onChange={e => setSoldForm(f => ({ ...f, customerName: e.target.value }))} />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Phone</Label>
                <Input placeholder="+971..." value={soldForm.customerPhone} onChange={e => setSoldForm(f => ({ ...f, customerPhone: e.target.value }))} />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Notes</Label>
              <Input placeholder="Payment method, details..." value={soldForm.notes} onChange={e => setSoldForm(f => ({ ...f, notes: e.target.value }))} />
            </div>
            <div className="flex justify-start gap-2 pt-2">
              <Button type="submit" disabled={marking} className="bg-green-600 hover:bg-green-700 text-white">
                {marking ? 'Processing...' : 'Confirm Sale & Deliver'}
              </Button>
              <Button type="button" variant="ghost" onClick={() => setSoldDialog(false)}>Cancel</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Watches;